import React from 'react'
import _ from 'lodash/fp'
import { connect } from 'react-redux'
import humps from 'humps'

// Reducers
import * as profileReducer from '../../reducers/profile'

// Material UI things
import withStyles from '@material-ui/core/styles/withStyles'
import Switch from '@material-ui/core/Switch'
import { Save } from '@material-ui/icons'

import ProfileForm from '../../components/profile/ProfileForm'
import ProfileImage from '../../components/profile/ProfileImage'
import ProfileVisibilityDialog from '../../components/profile/ProfileVisibilityDialog'
import OrganisationsEdit from './OrganisationsEdit'
import SplitContainer from '../../components/SplitContainer'

const styles = theme => ({
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  }
})

function ProfileEdit ({
  originalProfile,
  saveProfile,
  saveWaiting,
  handicapComparison,
  flipHandicapComparison,
  classes,
  ...props
}) {
  const [profile, setProfile] = React.useState() // TODO: reset when new is fetched?
  const [dialog, setDialog] = React.useState(false)

  React.useEffect(() => {
    setProfile(originalProfile)
    if (_.isEmpty(originalProfile)) {
      props.history.replace(`/login?returnTo=${props.location.pathname}`)
    }
  }, [originalProfile, props.history])

  const hasChanges = React.useMemo(() => !_.isEqual(originalProfile, profile), [originalProfile, profile])

  const fieldUpdater = React.useCallback(field => event => {
    if (_.get('target', event)) {
      const value = event.target.value
      setProfile(_.set(field, value))
    } else {
      setProfile(_.set(field, event))
    }
  }, [setProfile])

  const handleClickSave = React.useCallback(() => {
    if (!_.isEqual(_.get('visibility', originalProfile), _.get('visibility', profile))) {
      setDialog(true)
    } else {
      saveProfile(profile)
    }
  }, [originalProfile, profile, saveProfile, setDialog])

  return (
    <SplitContainer
      routed
      bottomBar={{
        loading: saveWaiting,
        hideLeft: true,
        rightText: 'save changes',
        rightIcon: (<Save />),
        disabledRight: !hasChanges || saveWaiting,
        onRightClick: handleClickSave
      }}
    >
      <div
        className={classes.fieldContainer}
      >
        <ProfileImage
          profile={profile}
        />

        <ProfileForm
          profile={profile}
          setProfile={setProfile}
          showPrivacySettings
        />

        <label style={{ color: 'rgba(0,0,0,0.54)', fontWeight: '400', alignSelf: 'flex-start', paddingTop: 16 }}>
          USE MY HANDICAP IN STATS
        </label>
        <Switch color='primary' checked={handicapComparison} onChange={flipHandicapComparison} />
        <p style={{ color: 'rgba(0,0,0,0.54)', fontWeight: '400', fontSize: '0.75rem', textAlign: 'left' }}>
          As a default, birdies, bogies and pars are calculated based on the actual par of the hole,
          but you can choose to have GLFR take your handicap into account when presenting your stats.
        </p>
      </div>

      <ProfileVisibilityDialog
        changeTo={_.get('visibility', profile)}
        open={dialog}
        handleClose={() => {
          fieldUpdater('visibility')(_.get('visibility', originalProfile))
          setDialog(false)
        }}
        handleAccept={() => {
          saveProfile(profile)
          setDialog(false)
        }}
      />
      <OrganisationsEdit classes={classes} profile={profile} {...props} />
    </SplitContainer>
  )
}

function ProfileLoader ({
  profile,
  loadProfile,
  saveProfile,
  ...props
}) {
  React.useEffect(() => {
    loadProfile()
  }, [loadProfile])

  const originalProfile = React.useMemo(() => humps.camelizeKeys(profile), [profile])

  const handleSaveProfile = React.useCallback(data => {
    saveProfile(humps.decamelizeKeys(data))
  }, [saveProfile])

  if (!profile) {
    return false
  }

  return (
    <ProfileEdit
      originalProfile={originalProfile}
      saveProfile={handleSaveProfile}
      {...props}
    />
  )
}

export default _.compose(
  withStyles(styles),
  connect(
    (s, _ownProps) => {
      const profile = profileReducer.selectors.getProfile(s)
      return {
        profile,
        saveWaiting: profileReducer.selectors.saveWaiting(s),
        handicapComparison: profileReducer.selectors.handicapComparison(s)
      }
    },
    (d, _ownProps) => {
      return {
        loadProfile: () => d(profileReducer.actions.loadRequest(Date.now())),
        saveProfile: (newProfile) => d(profileReducer.actions.saveRequest(newProfile)),
        flipHandicapComparison: () => d(profileReducer.actions.flipHandicapComparison())
      }
    }
  )
)(ProfileLoader)
